import React, { useEffect, useState, useCallback } from "react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { api, formatMoney, formatApiError } from "../../lib/api";
import PriceDisplay from "../../components/PriceDisplay";
import { useAuth } from "../../context/AuthContext";
import { useCurrency } from "../../context/CurrencyContext";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, X, Minus, Plus, MessageCircle, Trash2, Star } from "lucide-react";
import { carImageUrl } from "../../components/cars/CarCard";

const STATUS_BADGE = {
  accepted: { label: "Accepted", className: "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" },
  rejected: { label: "Rejected", className: "bg-rose-100 text-rose-800 hover:bg-rose-100" },
  expired: { label: "Expired", className: "bg-muted text-muted-foreground hover:bg-muted" },
};
// Labels for the two "pending" statuses depend on who is viewing — the
// party whose turn it is sees "Waiting on you", the other party sees
// "Waiting on <the other side>".
function statusMeta(status, isOwnerSide) {
  if (status === "pending_owner") {
    return isOwnerSide
      ? { label: "Waiting on you", className: "bg-blue-100 text-blue-800 hover:bg-blue-100" }
      : { label: "Waiting on owner", className: "bg-amber-100 text-amber-800 hover:bg-amber-100" };
  }
  if (status === "pending_customer") {
    return !isOwnerSide
      ? { label: "Waiting on you", className: "bg-blue-100 text-blue-800 hover:bg-blue-100" }
      : { label: "Waiting on renter", className: "bg-amber-100 text-amber-800 hover:bg-amber-100" };
  }
  return STATUS_BADGE[status] || { label: status, className: "bg-muted text-muted-foreground" };
}

export default function NegotiationsPage() {
  const { user } = useAuth();
  const { currencies } = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/negotiations/mine");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteNeg = async (negId) => {
    if (!window.confirm("Delete this negotiation?")) return;
    try {
      await api.delete(`/negotiations/${negId}`);
      setItems(prev => prev.filter(n => n.id !== negId));
      toast.success("Negotiation deleted");
    } catch(e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-display text-xl font-semibold tracking-tight">Price negotiations</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Offers you&apos;ve sent and received, for the total price of the trip. Deals expire after 24h of silence.
        </p>
      </div>
      {items.length === 0 ? (
        <Card className="border-dashed p-10 text-center">
          <p className="text-sm text-muted-foreground">
            No price negotiations yet. Browse cars and use <strong>&ldquo;Propose a price&rdquo;</strong> on any listing.
          </p>
          <Button asChild className="mt-4 bg-primary hover:bg-primary/90">
            <Link to="/cars">Browse cars</Link>
          </Button>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="negotiations-list">
          {items.map((n) => (
            <NegotiationCard key={n.id} n={n} currentUserId={user?.id} currencies={currencies} onChange={load} onDelete={deleteNeg} />
          ))}
        </div>
      )}
    </div>
  );
}

function NegotiationCard({ n, currentUserId, currencies, onChange, onDelete }) {
  const navigate = useNavigate();
  const [bookingBusy, setBookingBusy] = useState(false);
  const isOwnerSide = currentUserId === n.owner_id;
  const isMyTurn = (isOwnerSide && n.status === "pending_owner") || (!isOwnerSide && n.status === "pending_customer");
  const meta = statusMeta(n.status, isOwnerSide);
  const step = Math.max(500, Math.round(n.listing_price * 0.05));
  const ownRole = isOwnerSide ? "owner" : "customer";
  const ownHistory = (n.history || []).filter((h) => h.by === ownRole);
  const ownLastPrice = ownHistory.length ? ownHistory[ownHistory.length - 1].price : null;
  const minAllowed = isOwnerSide ? n.current_offer : (ownLastPrice ?? n.current_offer);
  const maxAllowed = isOwnerSide ? (ownLastPrice ?? n.listing_price) : n.current_offer;
  const [counterPrice, setCounterPrice] = useState(
    isOwnerSide ? Math.max(minAllowed, maxAllowed - step) : Math.min(maxAllowed, minAllowed + step)
  );
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const days = n.days || 1;

  // Whoever counters must go UP from current offer — no ceiling, matches backend
  const dec = () => setCounterPrice((p) => Math.max(minAllowed, p - step));
  const inc = () => setCounterPrice((p) => Math.min(maxAllowed, p + step));

  const act = async (op, body) => {
    setBusy(true);
    try {
      await api.post(`/negotiations/${n.id}/${op}`, body || {});
      if (op === "accept") {
        toast.success("Offer accepted! Redirecting to payment...");
        await goToCheckout();
      } else {
        toast.success(op === "reject" ? "Offer rejected." : "Counter sent.");
        onChange();
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const goToCheckout = async () => {
    setBookingBusy(true);
    try {
      const { data } = await api.post("/bookings", {
        car_id: n.car_id,
        start_date: n.start_date,
        end_date: n.end_date,
        neg_id: n.id,
      });
      navigate(`/checkout/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
      onChange();
    } finally {
      setBookingBusy(false);
    }
  };

  return (
    <Card className="border-border p-4 sm:p-5" data-testid={`neg-card-${n.id}`}>
      <div className="flex items-start gap-3 sm:gap-4">
        <Link to={`/cars/${n.car_id}`} className="shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden bg-muted">
          <img src={carImageUrl(n.car_image)} alt={n.car_title} className="w-full h-full object-cover" />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <Link to={`/cars/${n.car_id}`} className="font-display font-semibold text-sm sm:text-base tracking-tight hover:underline">
                {n.car_title}{n.car_year ? ` (${n.car_year})` : ""}
              </Link>
              <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
                {isOwnerSide ? `From ${n.customer_name}` : `Your offer`}
                {isOwnerSide && n.customer_rating != null && (
                  <span className="inline-flex items-center gap-0.5 text-amber-600" title={`${n.customer_rating_count} rating${n.customer_rating_count === 1 ? "" : "s"} from other owners`}>
                    <Star size={11} className="fill-amber-500 text-amber-500" /> {n.customer_rating.toFixed(1)}
                  </span>
                )}
                · Round {n.rounds_used}/{n.max_rounds}
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {n.start_date}{n.start_time ? ` ${n.start_time}` : ""} → {n.end_date}{n.end_time ? ` ${n.end_time}` : ""} · {days} day{days > 1 ? "s" : ""}
              </div>
            </div>
            <Badge className={meta.className} data-testid={`neg-status-${n.id}`}>{meta.label}</Badge>
          </div>

          <div className="mt-3 flex items-baseline gap-2 flex-wrap">
            <div className="text-xs text-muted-foreground">Current total offer</div>
            <div className="font-display text-xl sm:text-2xl font-bold tracking-tight" data-testid={`neg-current-${n.id}`}>
              <PriceDisplay amount={n.current_offer} listingCurrency={n.currency} />
            </div>
            <div className="text-xs text-muted-foreground">listed total: <PriceDisplay amount={n.listing_price} listingCurrency={n.currency} /></div>
          </div>

          {isMyTurn && (
            <div className="mt-4 border-t border-border pt-4 space-y-3">
              <div className="flex items-center gap-3">
                <button type="button" onClick={dec} disabled={busy} className="w-9 h-9 rounded-full border border-border bg-white hover:bg-muted flex items-center justify-center disabled:opacity-40">
                  <Minus size={14} />
                </button>
                <div className="flex-1 text-center font-display font-bold tabular-nums" data-testid={`neg-counter-input-${n.id}`}>
                  <PriceDisplay amount={counterPrice} listingCurrency={n.currency} />
                </div>
                <button type="button" onClick={inc} disabled={busy} className="w-9 h-9 rounded-full bg-[#2A9D8F] text-white hover:bg-[#23887A] flex items-center justify-center disabled:opacity-40">
                  <Plus size={14} />
                </button>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => act("accept")}
                  disabled={busy}
                  data-testid={`neg-accept-${n.id}`}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Check size={14} className="mr-1.5" /> Accept <PriceDisplay amount={n.current_offer} listingCurrency={n.currency} />
                </Button>
                <Button
                  onClick={() => act("counter", { counter_price: counterPrice })}
                  disabled={busy || counterPrice < minAllowed || counterPrice > maxAllowed}
                  variant="outline"
                  data-testid={`neg-counter-${n.id}`}
                  className="border-[#E97A48] text-[#E97A48] hover:bg-[#FCE9DC]"
                >
                  <MessageCircle size={14} className="mr-1.5" /> Counter
                </Button>
                <Button
                  onClick={() => act("reject")}
                  disabled={busy}
                  variant="ghost"
                  data-testid={`neg-reject-${n.id}`}
                  className="text-rose-600 hover:bg-rose-50"
                >
                  <X size={14} className="mr-1.5" /> Reject
                </Button>
              </div>
            </div>
          )}

          {n.status === "accepted" && (
            <div className="mt-3 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span>✅ Deal at <PriceDisplay amount={n.current_offer} listingCurrency={n.currency} /> total.</span>
                {!isOwnerSide && !n.booking_id && (
                  <Button size="sm" onClick={goToCheckout} disabled={bookingBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-3 text-xs">
                    {bookingBusy ? "Redirecting…" : "Proceed to payment →"}
                  </Button>
                )}
                {!isOwnerSide && n.booking_id && (
                  <Link className="underline" to={`/checkout/${n.booking_id}`}>Go to payment →</Link>
                )}
              </div>
            </div>
          )}
          {(n.status === "accepted" || n.status === "rejected" || n.status === "expired") && (
            <div className="mt-3 flex justify-end">
              <button type="button" onClick={() => onDelete(n.id)}
                className="text-xs text-destructive hover:underline flex items-center gap-1 opacity-70 hover:opacity-100">
                <Trash2 size={12} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
